import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  createSetupCommand,
  listInstallations,
  type McpInstallation,
  revokeInstallation,
  type SetupClient,
  type SetupCommand,
} from '../../api/mcp-quick-setup'
import { useDialog } from '../dialogs/useDialog'

export default function McpQuickSetup({ configured }: { configured: boolean }) {
  const dialog = useDialog()
  const [client, setClient] = useState<SetupClient>('codex')
  const [name, setName] = useState('')
  const [os, setOs] = useState('macos')
  const windows = os === 'windows'
  const [command, setCommand] = useState<SetupCommand | null>(null)
  const [busy, setBusy] = useState(false)
  const [installations, setInstallations] = useState<McpInstallation[]>([])
  const [loadError, setLoadError] = useState(false)
  const [expired, setExpired] = useState(false)
  const revision = useRef(0)
  const refresh = useCallback(async () => {
    try {
      setInstallations((await listInstallations()).installations)
      setLoadError(false)
    } catch {
      setLoadError(true)
    }
  }, [])
  const invalidate = useCallback(() => {
    revision.current++
  }, [])
  useEffect(() => {
    void refresh()
    return invalidate
  }, [refresh, invalidate])
  useEffect(() => {
    if (!command) return
    const update = () => setExpired(Date.now() >= Date.parse(command.expiresAt))
    update()
    const timer = setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [command])
  function clearCommand() {
    revision.current++
    setCommand(null)
    setExpired(false)
  }
  async function generate() {
    const current = ++revision.current
    setBusy(true)
    setCommand(null)
    try {
      const result = await createSetupCommand(client, name.trim())
      if (revision.current === current) {
        setCommand(result)
        setExpired(false)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not generate setup command')
    } finally {
      setBusy(false)
    }
  }
  async function revoke(item: McpInstallation) {
    if (
      !(await dialog.confirm({
        title: 'Revoke installation?',
        description: `${item.name} will lose MCP access. Other installations and OAuth connections remain available.`,
        confirmLabel: 'Revoke',
        tone: 'danger',
      }))
    )
      return
    try {
      await revokeInstallation(item.id)
      await refresh()
      toast.success('Installation revoked')
    } catch {
      toast.error('Could not revoke installation')
    }
  }
  return (
    <section
      className="space-y-4 rounded-xl border border-emerald-700/60 bg-harbor-surface p-5"
      aria-label="API key quick setup"
    >
      <h2 className="text-lg font-semibold text-harbor-text">Quick setup with API key</h2>
      <p className="text-sm text-harbor-text-secondary">
        Run one command on your computer. It adds a separate key and configures your client
        automatically. Requires Node.js 22 or newer; Claude Desktop also needs npx.
      </p>
      {client === 'opencode' && (
        <p className="text-sm text-harbor-text-secondary">
          Updates your global OpenCode configuration. Project configuration or OPENCODE_CONFIG
          overrides can take precedence; remove conflicting Context Harbor entries there.
        </p>
      )}
      {client === 'codex' && (
        <p className="text-sm text-harbor-text-secondary">
          This command configures Codex on this computer — the CLI, desktop app, and IDE extension
          share one configuration file. Hosted ChatGPT Work chat does not read local configuration —
          use the manual ChatGPT Work settings below.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-harbor-text">
          Client
          <select
            aria-label="Quick setup client"
            value={client}
            onChange={(e) => {
              setClient(e.target.value as SetupClient)
              clearCommand()
            }}
            className="mt-1 block w-full rounded border border-harbor-border bg-harbor-surface-raised p-2"
          >
            <option value="codex">Codex</option>
            <option value="claude-desktop">Claude Desktop</option>
            <option value="opencode">OpenCode</option>
          </select>
        </label>
        <label className="text-sm text-harbor-text">
          Installation name
          <input
            value={name}
            maxLength={80}
            onChange={(e) => {
              setName(e.target.value)
              clearCommand()
            }}
            placeholder="Work laptop"
            className="mt-1 block w-full rounded border border-harbor-border bg-harbor-surface-raised p-2"
          />
        </label>
        <label className="text-sm text-harbor-text">
          Operating system
          <select
            value={os}
            onChange={(e) => setOs(e.target.value)}
            className="mt-1 block w-full rounded border border-harbor-border bg-harbor-surface-raised p-2"
          >
            <option value="macos">macOS</option>
            <option value="linux">Linux (Ubuntu)</option>
            <option value="windows">Windows PowerShell</option>
          </select>
        </label>
      </div>
      <button
        type="button"
        disabled={!configured || !name.trim() || busy}
        onClick={() => void generate()}
        className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {busy ? 'Generating…' : 'Generate setup command'}
      </button>
      {!configured && (
        <p className="text-sm text-amber-200">Configure the remote MCP server URL first.</p>
      )}
      {command && (
        <div className="space-y-2">
          <p className="text-sm text-harbor-text-secondary">
            {expired
              ? 'This command expired. Generate another to start setup.'
              : `Single use. Expires at ${new Date(command.expiresAt).toLocaleTimeString()}. Keep it private until used.`}
          </p>
          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-harbor-surface-raised p-3 text-xs text-harbor-text">
            {windows ? command.powershellCommand : command.command}
          </pre>
          <button
            type="button"
            disabled={expired}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(
                  windows ? command.powershellCommand : command.command
                )
                toast.success('Setup command copied')
              } catch {
                toast.error('Could not copy command')
              }
            }}
            className="rounded bg-harbor-border px-4 py-2 text-sm text-harbor-text disabled:opacity-50"
          >
            Copy setup command
          </button>
          <p className="text-xs text-harbor-text-secondary">
            The installer backs up your configuration, saves a private recovery file, and checks MCP
            access. Restart your client when it finishes. Codex TOML formatting and comments may be
            normalized.
          </p>
        </div>
      )}
      <div className="space-y-2 border-t border-harbor-border pt-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-harbor-text">Your installations</h3>
          <button type="button" onClick={() => void refresh()} className="text-sm text-emerald-400">
            Refresh installations
          </button>
        </div>
        {loadError && (
          <p role="alert" className="text-sm text-amber-200">
            Could not load installations. Try refreshing.
          </p>
        )}
        {!loadError && installations.length === 0 && (
          <p className="text-sm text-harbor-text-secondary">
            Installations appear after you run a setup command.
          </p>
        )}
        {installations.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between gap-3 rounded bg-harbor-surface-raised p-3"
          >
            <div className="min-w-0">
              <p className="break-words text-sm text-harbor-text">{item.name}</p>
              <p className="text-xs text-harbor-text-secondary">
                {item.client} · {item.tokenPrefix}…
              </p>
            </div>
            <button
              type="button"
              onClick={() => void revoke(item)}
              className="text-sm text-red-400"
            >
              Revoke
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-harbor-text-secondary">
        For hosted ChatGPT, use the OAuth connection option. Manual client configuration remains
        available below.
      </p>
    </section>
  )
}
